import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, Signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { OnlinePlayOnlineGameService } from '@core/online-play/services/online-play-online-game.service';
import {
    BackgroundColorDirective,
    ButtonDirective,
    ColorDirective,
    DCColorInput,
    DcRangeSingleValue,
    DcRangeValue,
    DcThemeService,
    RangeSliderComponent,
    TypographyDirective,
} from '@dartcounter/ui';
import { GameMode } from '@dc-core/dc-backend/dc-enums';
import { IonMenu, MenuController } from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { PreferenceService } from 'dc-core/dc-services/preference/preference.service';
import { GA_EVENTCATEGORIES } from 'src/app/app.globals';
import { AppFeaturesService } from 'src/app/core/app-features/services/app-features.service';
import { DartBotSettingsComponent } from 'src/app/modules/dartbot/dialogs/dartbot-settings/dartbot-settings.dialog';
import { DartBotSettingsService } from 'src/app/modules/dartbot/services/dartbot-settings.service';
import { gameModeHasDartBot } from 'src/app/modules/dartbot/utils/dartbot.utils';
import { EntryModePipe } from 'src/app/modules/games/pipes/entry-mode.pipe';
import { FinishModePipe } from 'src/app/modules/games/pipes/finish-mode.pipe';
import { InGameActionReplaysService } from 'src/app/modules/games/services/in-game-action-replays.service';
import { InGameRecordingService } from 'src/app/modules/games/services/in-game-recording.service';
import { SpeechToScoreInfoDialogComponent } from 'src/app/modules/speech-to-score/dialogs/speech-to-score-info/speech-to-score-info.dialog';
import { JanusStateService } from 'src/app/services/janus/janus-state.service';
import { UpgradeService } from 'src/app/services/upgrade.service';
import { OptionButtonComponent } from 'src/app/shared/components/option-button/option-button.component';
import { ToggleSwitchComponent } from 'src/app/shared/components/toggle-switch/toggle-switch.component';
import { HideScrollbarDirective } from 'src/app/shared/directives/hide-scrollbar.directive';
import { ModalService } from 'src/app/shared/services/modal.service';
import { fullscreenDialogClasses } from 'src/app/shared/utils/dialog.utils';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/services/auth.service';

import { InGameMenuThemeService } from './in-game-menu-theme.service';

@Component({
    selector: 'app-in-game-menu',
    templateUrl: 'in-game-menu.component.html',
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,
        IonMenu,
        OptionButtonComponent,
        EntryModePipe,
        FinishModePipe,
        ToggleSwitchComponent,
        ButtonDirective,
        BackgroundColorDirective,
        ColorDirective,
        TypographyDirective,
        FormsModule,
        RangeSliderComponent,
        HideScrollbarDirective,
    ],
})
export class InGameMenuComponent implements OnInit {
    public authService: AuthService = inject(AuthService);
    public preferenceService: PreferenceService = inject(PreferenceService);
    public appFeaturesService: AppFeaturesService = inject(AppFeaturesService);

    private modalService: ModalService = inject(ModalService);
    private janusStateService: JanusStateService = inject(JanusStateService);
    private inGameRecordingService: InGameRecordingService = inject(InGameRecordingService);
    private inGameActionReplaysService: InGameActionReplaysService = inject(InGameActionReplaysService);
    private upgradeService: UpgradeService = inject(UpgradeService);
    private themeService: DcThemeService = inject(DcThemeService);
    private dartBotSettingsService: DartBotSettingsService = inject(DartBotSettingsService);
    private menuController: MenuController = inject(MenuController);
    private onlinePlayOnlineGameService: OnlinePlayOnlineGameService = inject(OnlinePlayOnlineGameService);
    private inGameMenuThemeService: InGameMenuThemeService = inject(InGameMenuThemeService);

    public inGameMode: Signal<GameMode> = computed(() => this.preferenceService.inGameMode());
    public inGameDetails: Signal<any> = computed(() => this.preferenceService.inGameDetails());
    public isOnlineGroupGame: Signal<boolean> = computed(
        () =>
            !!this.inGameDetails()?.is_online &&
            (this.inGameDetails()?.teams?.reduce(
                (playerCount: number, team: { users?: unknown[] }) => playerCount + (team.users?.length ?? 0),
                0
            ) ?? 0) > 2
    );
    public displayedDartBotTimeout: Signal<number> = computed(() =>
        this.preferenceService.enableDartBotTimeout() ? this.preferenceService.dartbotTimeout() : 0
    );

    public hasDartBotSettings: Signal<boolean> = computed(() => gameModeHasDartBot(this.inGameMode()));
    public hasDartBot: Signal<boolean> = computed(() =>
        this.dartBotSettingsService
            .dartbots()
            .some((bot) => bot.getConfig().gameMode === this.preferenceService.inGameMode())
    );
    public accessibilityMode: Signal<boolean> = computed(() => this.themeService.accessibilityMode());

    public dartBotOverallSettings: Signal<{
        callDartBotYouRequire: boolean;
        autocompleteDartBotGame: boolean;
    }> = computed(() => DartBotSettingsComponent.getDartBotOverallSettings(this.inGameMode()));
    public menuTextColor: Signal<DCColorInput> = computed(() => this.inGameMenuThemeService.textColor());
    public menuBackgroundColor: Signal<DCColorInput> = computed(() => this.inGameMenuThemeService.backgroundColor());
    public menuSectionHeaderBackgroundColor: Signal<DCColorInput> = computed(() =>
        this.inGameMenuThemeService.sectionHeaderBackgroundColor()
    );
    public menuSectionBackgroundColor: Signal<DCColorInput> = computed(() =>
        this.inGameMenuThemeService.sectionBackgroundColor()
    );

    public hasCameraEnabled: Signal<boolean> = computed(() => this.janusStateService.hasCameraEnabled());

    public readonly version = environment.version;
    public readonly isWeb: boolean = environment.isWeb;

    public isTeamGame: Signal<boolean> = computed(() => this.onlinePlayOnlineGameService.isTeamGame());

    public ngOnInit(): void {
        if (this.preferenceService.dartbotTimeout() === 0) {
            this.preferenceService.enableDartBotTimeout.set(false);
        }
    }

    public async openUpgradeDialog(): Promise<void> {
        const dialogComponent = await this.upgradeService.getUpgradeDialog(GA_EVENTCATEGORIES.INGAMEMENU);
        this.modalService.openModal({
            component: dialogComponent,
            cssClass: fullscreenDialogClasses,
        });
    }

    public toggleShowCheckoutHint(): void {
        this.togglePreference(this.preferenceService.showCheckoutHint);
    }

    public toggleShowInGameChat(): void {
        this.togglePreference(this.preferenceService.showInGameChat);
    }

    public toggleEnableInGameVoiceCall(): void {
        this.togglePreference(this.preferenceService.enableInGameVoiceCall);
    }

    public toggleShowConfirmThrowout(): void {
        this.togglePreference(this.preferenceService.showConfirmThrowout);
    }

    public toggleConfirmUndoTurn(): void {
        this.togglePreference(this.preferenceService.confirmUndoTurn);
    }

    public toggleShowSpectatorCount(): void {
        this.togglePreference(this.preferenceService.showSpectatorCount);
    }

    public toggleAutoSubmitScore(): void {
        this.togglePreference(this.preferenceService.autoSubmitScore);
    }

    public toggleAllowCaller(): void {
        this.togglePreference(this.preferenceService.allowCaller);
    }

    public toggleCallYouRequire(): void {
        this.togglePreference(this.preferenceService.callYouRequire);
    }

    public toggleCallDartBot(): void {
        this.togglePreference(this.preferenceService.callDartBot);
    }

    public toggleCallDartBotYouRequire(): void {
        this.togglePreference(this.preferenceService.callDartBotYouRequire);
    }

    public toggleAutocompleteDartBot(): void {
        this.togglePreference(this.preferenceService.autocompleteDartBot);
    }

    public toggleCallOmniImmediately(): void {
        this.togglePreference(this.preferenceService.callOmniImmediately);
    }

    public toggleShowAvg(): void {
        this.togglePreference(this.preferenceService.showAvg);
    }

    public toggleActionReplays(): void {
        this.preferenceService.enableActionReplays.update((enableActionReplays) => !enableActionReplays);
        this.preferenceService.savePreferences();

        if (this.preferenceService.enableActionReplays()) {
            this.inGameActionReplaysService.checkStartingOnlineActionReplayRecording(this.authService.user.id, true);
        } else {
            this.inGameActionReplaysService.resetActionReplayData();
            this.inGameRecordingService.stopRecording(this.authService.user.id, false).catch(console.error);
        }
    }

    public togglePromptActionReplay(): void {
        this.togglePreference(this.preferenceService.promptActionReplay);
    }

    public updateDartBotTimeout(value: DcRangeValue): void {
        const timeout = value as DcRangeSingleValue;

        this.preferenceService.enableDartBotTimeout.set(timeout === 0 ? false : true);
        this.preferenceService.dartbotTimeout.set(timeout);
        this.preferenceService.savePreferences();
    }

    public toggleAccessibilityMode(): void {
        this.themeService.toggleAccessibilityMode();
    }

    public showSpeechToScore(): void {
        this.modalService.openModal({
            component: SpeechToScoreInfoDialogComponent,
            cssClass: fullscreenDialogClasses,
        });
    }

    public disableMenuSwipeGesture(): void {
        this.menuController.swipeGesture(false, 'ingamemenu');
    }

    public enableMenuSwipeGesture(): void {
        this.menuController.swipeGesture(true, 'ingamemenu');
    }

    private togglePreference(preference: WritableSignal<boolean>): void {
        preference.update((enabled) => !enabled);
        this.preferenceService.savePreferences();
    }
}
